import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart2, Calendar, Search, ArrowUpDown, Trash2, Eye } from 'lucide-react';
import { getBacktestsList, deleteBacktest, SavedBacktest } from '@/services/backtestStorageService';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { BacktestDetailsView } from './BacktestDetailsView';
import { supabase } from '@/integrations/supabase/client';

export const BacktestHistoryView = () => {
  const { toast } = useToast();
  const [backtests, setBacktests] = useState<SavedBacktest[]>([]);
  const [filteredBacktests, setFilteredBacktests] = useState<SavedBacktest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<'date' | 'performance'>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [selectedBacktest, setSelectedBacktest] = useState<SavedBacktest | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [deleteConfirmationId, setDeleteConfirmationId] = useState<string | null>(null);

  // Fetch the list of backtests on component mount
  useEffect(() => {
    loadBacktests();
  }, []);

  // Get the current user's ID
  const getUserId = async () => {
    const { data } = await supabase.auth.getUser();
    return data.user?.id;
  };

  // Load backtests from the database
  const loadBacktests = async () => {
    setLoading(true);
    
    try {
      const userId = await getUserId();
      if (!userId) {
        toast({
          title: "Authentication Error",
          description: "You need to be logged in to view your backtest history.",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      const backtestsList = await getBacktestsList(userId);
      setBacktests(backtestsList);
      filterAndSortBacktests(backtestsList);
      
      toast({
        title: "Backtests Loaded",
        description: `Loaded ${backtestsList.length} backtests from your history.`,
      });
    } catch (error) {
      console.error("Error loading backtests:", error);
      toast({
        title: "Error",
        description: "Failed to load backtest history.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort backtests
  useEffect(() => {
    filterAndSortBacktests(backtests);
  }, [searchTerm, statusFilter, sortField, sortDirection]);

  const filterAndSortBacktests = (backtests: SavedBacktest[]) => {
    let filtered = [...backtests];

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        backtest => 
          backtest.name.toLowerCase().includes(term) ||
          backtest.description?.toLowerCase().includes(term) ||
          backtest.strategyName?.toLowerCase().includes(term)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(backtest => backtest.status === statusFilter);
    }

    // Sort the results
    filtered.sort((a, b) => {
      if (sortField === 'date') {
        return sortDirection === 'asc'
          ? new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      } else {
        // Performance sorting (by total return)
        const aReturn = a.performance?.totalReturn || 0;
        const bReturn = b.performance?.totalReturn || 0;
        return sortDirection === 'asc' ? aReturn - bReturn : bReturn - aReturn;
      }
    });

    setFilteredBacktests(filtered);
  };

  // Toggle sort direction
  const toggleSort = (field: 'date' | 'performance') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Open details dialog for a backtest
  const viewBacktestDetails = (backtest: SavedBacktest) => {
    setSelectedBacktest(backtest);
    setShowDetailsDialog(true);
  };

  // Delete a backtest
  const confirmDeleteBacktest = (id: string) => {
    setDeleteConfirmationId(id);
  };

  const handleDeleteBacktest = async () => {
    if (!deleteConfirmationId) return;
    
    try {
      const success = await deleteBacktest(deleteConfirmationId);
      
      if (success) {
        setBacktests(prev => prev.filter(b => b.id !== deleteConfirmationId));
        setFilteredBacktests(prev => prev.filter(b => b.id !== deleteConfirmationId));
        
        toast({
          title: "Backtest Deleted",
          description: "The backtest has been permanently deleted.",
        });
      } else {
        throw new Error("Failed to delete backtest");
      }
    } catch (error) {
      console.error("Error deleting backtest:", error);
      toast({
        title: "Error",
        description: "Failed to delete the backtest.",
        variant: "destructive",
      });
    } finally {
      setDeleteConfirmationId(null);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Render backtest status badge
  const renderStatusBadge = (status: string) => {
    let className = '';
    
    switch (status) {
      case 'completed':
        className = 'bg-green-100 text-green-800 border-green-200';
        break;
      case 'running':
        className = 'bg-blue-100 text-blue-800 border-blue-200';
        break;
      case 'paused':
        className = 'bg-yellow-100 text-yellow-800 border-yellow-200';
        break;
      case 'error':
        className = 'bg-red-100 text-red-800 border-red-200';
        break;
      default:
        className = 'bg-gray-100 text-gray-800 border-gray-200';
    }
    
    return (
      <Badge variant="outline" className={`${className} capitalize`}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <CardTitle className="text-2xl">Backtest History</CardTitle>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={loadBacktests}
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                    Loading...
                  </span>
                ) : 'Refresh'}
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-grow">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Search backtests..."
                className="pl-9"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : filteredBacktests.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <BarChart2 className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No backtests found</h3>
              <p className="text-sm text-gray-500 mb-4 max-w-md text-center">
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your filters to see more results'
                  : 'Run your first backtest to see results here'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>
                      <button 
                        className="flex items-center" 
                        onClick={() => toggleSort('date')}
                      >
                        Date
                        <ArrowUpDown className={`ml-1 h-4 w-4 ${sortField === 'date' ? 'opacity-100' : 'opacity-30'}`} />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button 
                        className="flex items-center" 
                        onClick={() => toggleSort('performance')}
                      >
                        Return
                        <ArrowUpDown className={`ml-1 h-4 w-4 ${sortField === 'performance' ? 'opacity-100' : 'opacity-30'}`} />
                      </button>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Strategy</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBacktests.map((backtest) => (
                    <TableRow key={backtest.id}>
                      <TableCell className="font-medium">{backtest.name}</TableCell>
                      <TableCell className="text-sm">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                          {formatDate(backtest.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell className={`font-medium ${
                        (backtest.performance?.totalReturn || 0) >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {(backtest.performance?.totalReturn || 0) >= 0 ? '+' : ''}
                        {(backtest.performance?.totalReturn || 0).toFixed(2)}%
                      </TableCell>
                      <TableCell>{renderStatusBadge(backtest.status)}</TableCell>
                      <TableCell>{backtest.strategyName || 'N/A'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => viewBacktestDetails(backtest)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => confirmDeleteBacktest(backtest.id)}
                            title="Delete Backtest"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Backtest Details Dialog */}
      {selectedBacktest && (
        <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
          <DialogContent className="max-w-screen-lg h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{selectedBacktest.name}</DialogTitle>
            </DialogHeader>
            <BacktestDetailsView backtestId={selectedBacktest.id} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailsDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirmationId} onOpenChange={(open) => !open && setDeleteConfirmationId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Backtest</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete this backtest? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmationId(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteBacktest}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};